import { useEffect, useMemo, useState } from "react";

// Importing formik & yup
import { Formik } from "formik";
import * as Yup from "yup";

import { useSelector, useDispatch } from "react-redux";

// Importing bootstrap components
import { Row, Col, Card, Table, Form, Button, Spinner } from "react-bootstrap";

// Import react-select
import Select from "react-select";

// Import toast utilities
import { showSuccessToast } from "utils/toast";

import HRPagination from "components/HRPagination";

// User Query
import { setEmployees } from "state/slices/user/userSlice";

import { useGetUsersQuery } from "state/slices/user/userApiSlice";

import { setTemplates } from "state/slices/mail/mailSlice";
import {
  useDeleteMailTemplateMutation,
  useGetMailTemplatesQuery,
  useSaveMailTemplateMutation,
  useUpdateMailTemplateMutation,
} from "state/slices/mail/mailApiSlice";

// Importing mail slices
import {
  addTemplate as addTemplateInStore,
  updateTemplate as updateTemplateInStore,
  deleteTemplate as deleteTemplateInStore,
} from "state/slices/mail/mailSlice";

const initialValues = {
  subject: "",
  recipients: [],
  content: "",
  saveAsTemplate: false,
  templateName: "",
};

const MailManagement = () => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [formikValues, setFormikValues] = useState(initialValues);

  const [deleteTemplate] = useDeleteMailTemplateMutation();
  const [saveMailTemplate] = useSaveMailTemplateMutation();
  const [updateMailTemplate] = useUpdateMailTemplateMutation();

  const employees = useSelector((state) => state.employee.employees);
  const recipientsArray = useMemo(() => {
    const allEmployeesOption = { employeeId: "all", name: "All Employees" };
    return [allEmployeesOption, ...employees].map((employee) => {
      return {
        value: employee.employeeId,
        label: employee.name,
      };
    });
  }, [employees]);

  const dispatch = useDispatch();
  const { data: users } = useGetUsersQuery();

  useEffect(() => {
    if (users) {
      dispatch(setEmployees(users));
    }
  }, [users, dispatch]);

  // Fetch templates
  const {
    data,
    refetch,
    isLoading: isTemplatesLoading,
  } = useGetMailTemplatesQuery();

  const templates = useSelector((state) => state.mailTemplate.templates);

  useEffect(() => {
    if (data) {
      dispatch(setTemplates(data.templates));
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, [templates]);

  // Pagination states
  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.ceil(templates.length / ITEMS_PER_PAGE) || 0;
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate the start and end index for the current page
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;

  const validationSchema = Yup.object().shape({
    subject: Yup.string().required("Subject is required"),
    recipients: Yup.array().min(1, "At least one recipient must be selected"),
    content: Yup.string().required("Content is required"),
    saveAsTemplate: Yup.boolean(),
    templateName: Yup.string().when("saveAsTemplate", {
      is: (val) => val === true,
      then: (schema) =>
        schema.required("Template name is required when saving as template"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleCheckboxChange = (event, row) => {
    if (event.target.checked) {
      setSelectedRow(row);
    } else {
      setSelectedRow(null);
      setFormikValues(initialValues);
    }
  };

  let actionType = "new";
  const handleSubmit = async (values, { resetForm }) => {
    try {
      if (actionType === "new" && values.id) {
        delete values.id;
      }

      let isSentToAllReceipients = values.recipients.some(
        (obj) => obj.value === "all"
      );

      const payload = {
        ...values,
        recipients: isSentToAllReceipients
          ? recipientsArray.filter((obj) => obj.value !== "all")
          : values.recipients,
      };

      const request =
        actionType === "update"
          ? updateMailTemplate(payload)
          : saveMailTemplate(payload);

      const data = await request.unwrap();

      if (values.saveAsTemplate) {
        if (actionType === "update") {
          dispatch(updateTemplateInStore(data.template));
        } else {
          dispatch(addTemplateInStore(data.template));
        }
      }

      // Reset formikValues to initial values after successful creation
      setFormikValues(initialValues);

      if (selectedRow?.id) {
        setSelectedRow(null);
      }

      showSuccessToast("Mail sent successfully.");
      resetForm();
    } catch (err) {
      // Error toast is automatically shown by baseQuery
      console.log(err);
    }
  };

  const handleEdit = () => {
    setFormikValues({
      ...selectedRow,
      saveAsTemplate: true,
      templateName: selectedRow.name || "",
    });
  };

  const deleteTemplateManage = async () => {
    try {
      const rowId = selectedRow.id;

      await deleteTemplate(rowId).unwrap();

      // Update localTemplates on successful deletion
      dispatch(deleteTemplateInStore({ id: rowId }));

      showSuccessToast("Mail template deleted successfully.");
    } catch (err) {
      // Error toast is automatically shown by baseQuery
    }

    if (selectedRow.id) {
      // Reset formik data
      setFormikValues(initialValues);

      // Reset row data
      setSelectedRow(null);
    }
  };

  return (
    <section className="section dashboard">
      <Row>
        <Col md={6}>
          <Card>
            <Card.Body>
              <Formik
                enableReinitialize={true}
                initialValues={formikValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  handleChange,
                  handleSubmit,
                  touched,
                  errors,
                  setFieldValue,
                  isSubmitting,
                }) => {
                  const isAllSelected = values.recipients?.some((r) => r.value === "all");

                  // Conditionally filter options
                  const filteredOptions = isAllSelected
                    ? recipientsArray.filter((option) => option.value === "all")
                    : recipientsArray;

                  return (
                    <Form noValidate onSubmit={handleSubmit} className="mt-3">
                      <Form.Group className="form-group required mb-4" controlId="recipients">
                        <Form.Label className="control-label fw-medium mb-3">Recipients</Form.Label>
                        <Select
                          name="recipients"
                          options={filteredOptions}
                          value={values.recipients}
                          onChange={(selectedOptions) => {
                            if (
                              selectedOptions &&
                              selectedOptions.some((opt) => opt.value === "all")
                            ) {
                              setFieldValue("recipients", [recipientsArray[0]]);
                            } else {
                              setFieldValue("recipients", selectedOptions);
                            }
                          }}
                          isMulti
                          className={
                            touched.recipients && errors.recipients
                              ? "is-invalid"
                              : ""
                          }
                        />
                        {touched.recipients && errors.recipients && (
                          <div className="invalid-feedback d-block">
                            {errors.recipients}
                          </div>
                        )}
                      </Form.Group>

                      <Form.Group className="form-group required mb-4" controlId="subject">
                        <Form.Label className="control-label fw-medium mb-3">Subject</Form.Label>
                        <Form.Control
                          type="text"
                          name="subject"
                          placeholder="Enter subject"
                          value={values.subject}
                          onChange={handleChange}
                          isInvalid={touched.subject && !!errors.subject}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.subject}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="form-group required mb-4" controlId="content">
                        <Form.Label className="control-label fw-medium mb-3">Content</Form.Label>
                        <Form.Control
                          as="textarea"
                          name="content"
                          placeholder="Enter content"
                          value={values.content}
                          rows={5}
                          onChange={handleChange}
                          isInvalid={touched.content && !!errors.content}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.content}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="form-group required mb-4" controlId="saveAsTemplate">
                        <Form.Check
                          type="checkbox"
                          label="Save this as template?"
                          name="saveAsTemplate"
                          checked={values.saveAsTemplate}
                          onChange={handleChange}
                        />
                      </Form.Group>

                      {values.saveAsTemplate && (
                        <Form.Group className="form-group required mb-4" controlId="templateName">
                          <Form.Label className="control-label fw-medium mb-3">
                            Template Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="templateName"
                            placeholder="Enter template name"
                            value={values.templateName}
                            onChange={handleChange}
                            isInvalid={
                              touched.templateName && !!errors.templateName
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.templateName}
                          </Form.Control.Feedback>
                        </Form.Group>
                      )}

                      <div className="d-flex align-items-center gap-3">
                        <Button
                          variant="primary"
                          disabled={isSubmitting}
                          type="submit"
                          onClick={() =>
                            (actionType = selectedRow?.id ? "update" : "new")
                          }
                        >
                          <span className="d-flex align-items-center justify-content-center">
                            {isSubmitting && (
                              <Spinner
                                animation="border"
                                size="sm"
                                role="status"
                                className="me-2"
                                style={{ width: "18px", height: "18px" }}
                              />
                            )}
                            {isSubmitting ? "Sending..." : "Send"}{" "}
                            {values.saveAsTemplate ? "& Save Template" : ""}
                          </span>
                        </Button>

                        {values.id && values.saveAsTemplate && (
                          <Button
                            variant="secondary"
                            type="submit"
                            value="new"
                            disabled={isSubmitting}
                            onClick={() => (actionType = "new")}
                          >
                            Send{" "}
                            {values.saveAsTemplate
                              ? "& Save as new template"
                              : ""}
                          </Button>
                        )}
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Table striped bordered hover responsive className="text-center">
            <thead>
              <tr>
                <th></th>
                <th>S.No</th>
                <th>Template</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {templates.length > 0 ? (
                templates
                  .slice(indexOfFirstItem, indexOfLastItem)
                  .map((row, index) => (
                    <tr key={row.id}>
                      <td>
                        <Form.Check
                          type="checkbox"
                          name="row"
                          value={row.id}
                          checked={selectedRow?.id === row.id}
                          onChange={(e) => handleCheckboxChange(e, row)}
                        />
                      </td>
                      <td>{index + 1}</td>
                      <td>{row.name}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="me-2"
                          disabled={!selectedRow || selectedRow?.id !== row.id}
                          onClick={handleEdit}
                        >
                          <i className="bi bi-pencil-square"></i>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          disabled={!selectedRow || selectedRow?.id !== row.id}
                          onClick={deleteTemplateManage}
                        >
                          <i className="bi bi-trash3"></i>
                        </Button>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr className="bg-light border text-center">
                  <td colSpan={6} className="text-muted">
                    {isTemplatesLoading ? "Loading Data..." : "No Data Found"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          {/* Pagination */}
          <div className="d-flex justify-content-end">
            <HRPagination
              totalPages={totalPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </Col>
      </Row>
    </section>
  );
};

export default MailManagement;
